import React, { FC, useEffect, useState, useRef, useCallback } from 'react'
import cn from 'classnames'
import { WbIcon } from '@workbench/react/dist'
import classes from './FilterOptions.module.scss'
import { Option } from './types'

interface FilterOptionsProps {
  options: Option[]
  onSelect: (option: Option) => void
  onRequestClose?: () => void
  open?: boolean
}

const underlineMatch = (filter: string, string: string) => {
  const re = new RegExp(`(${filter})`, 'i')
  return string.split(re).map((s) => (s.match(re) ? <u>{s}</u> : s))
}

export const FilterOptions: FC<FilterOptionsProps> = ({
  options,
  onSelect,
  open = true,
  onRequestClose = () => {},
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState<string>('')

  useEffect(() => {
    if (open) {
      inputRef?.current?.focus()
    } else {
      setValue('')
    }
  }, [inputRef, open])

  const handleGlobalClick = useCallback(
    (event: MouseEvent) => {
      if (
        inputRef.current?.parentElement &&
        !inputRef.current.parentElement.contains(event.target as HTMLElement)
      ) {
        onRequestClose()
      }
    },
    [inputRef, onRequestClose]
  )

  useEffect(() => {
    if (!open) {
      return
    }

    document.addEventListener('click', handleGlobalClick)
    return () => {
      document.removeEventListener('click', handleGlobalClick)
    }
  }, [open, handleGlobalClick])

  const filterOption = (option: Option) =>
    value.length === 0 ||
    option.label.toLowerCase().includes(value.toLowerCase())
  const filteredOptions = options.filter(filterOption)
  return (
    <div className={cn(classes.container, { [classes.open]: open })}>
      <label className={classes.label}>
        <WbIcon className={classes.icon} name="search" />

        <input
          ref={inputRef}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          className={classes.input}
        />
      </label>

      <div className={classes.items}>
        {filteredOptions.map((option) => (
          <button
            type="button"
            key={option.value}
            className={classes.item}
            onClick={() => onSelect(option)}
          >
            {value.length > 0
              ? underlineMatch(value, option.label)
              : option.label}
          </button>
        ))}
      </div>
    </div>
  )
}
